import chatService from './chatService';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../store/uiModalStore';
import { toExportRow, exportFileName, EXPORT_FORMATS } from '../utils/chatExportModel';
import { FORMATTERS } from '../utils/chatExportFormats';
import { memberInfo } from '../utils/chatSearchQuery';
import { saveTextFile } from '../utils/saveTextFile';

/**
 * Export a conversation's history to a file (PLAN §8).
 *
 * Pages GET /chat/channels/:id/export (500 rows, ascending) — a plan-gated
 * endpoint, so the paid gate holds for every client; the "Pro" badge in the
 * menu is only a hint (D13/D15). A 402 closes the flow into the upgrade
 * modal with the server's reason, and nothing is saved. The pages never touch
 * the live chat buffer or its disk cache.
 *
 * Rows go through the whitelist `toExportRow` (secret label only, no share
 * token, no attachment URL) and one of the pure formatters. Capped at
 * MAX_EXPORT_MESSAGES (D17); the file then says it was cut.
 *
 * → { status: 'saved' | 'cancelled' | 'upgrade' | 'error', count?, truncated?, path? }
 */

export const EXPORT_PAGE_SIZE = 500;
export const MAX_EXPORT_MESSAGES = 100000;
// A 429 mid-export retries the SAME page instead of discarding what was
// already fetched (the limit is 120 pages/min, so a >60k-message channel on a
// fast link can hit it). Waits Retry-After when the server sends one, else
// 1 s, 2 s, 4 s …; each wait is capped, and after MAX_RATE_RETRIES the export
// fails with its own message.
export const MAX_RATE_RETRIES = 4;
const MAX_WAIT_MS = 60000;

const isCancel = (err) => err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';

class RateLimitedError extends Error {}

function retryAfterMs(err, attempt) {
  const headers = err?.response?.headers || {};
  const key = Object.keys(headers).find((k) => k.toLowerCase() === 'retry-after');
  const secs = key != null ? Number(headers[key]) : NaN;
  const ms = Number.isFinite(secs) && secs >= 0 ? secs * 1000 : 1000 * 2 ** attempt;
  return Math.min(ms, MAX_WAIT_MS);
}

const defaultSleep = (ms, signal) =>
  new Promise((resolve) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener?.('abort', () => {
      clearTimeout(id);
      resolve();
    });
  });

async function fetchPageWithRetry(channelId, afterSeq, { signal, sleep, onRateLimited }) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await chatService.exportPage(channelId, { afterSeq, limit: EXPORT_PAGE_SIZE }, { signal });
    } catch (err) {
      if (err?.response?.status !== 429 || signal?.aborted) throw err;
      if (attempt >= MAX_RATE_RETRIES) throw new RateLimitedError();
      onRateLimited?.();
      // eslint-disable-next-line no-await-in-loop
      await sleep(retryAfterMs(err, attempt), signal);
    }
  }
}

export async function runChatExport({
  channel,
  channelName,
  format = 'markdown',
  signal,
  onProgress,
  t,
  maxMessages = MAX_EXPORT_MESSAGES,
  now = () => new Date(),
  sleep = defaultSleep,
  onRateLimited,
}) {
  const notify = useNotificationStore.getState();
  const rows = [];
  let afterSeq = 0;
  let truncated = false;
  let historyDays = null;

  try {
    for (;;) {
      if (signal?.aborted) return { status: 'cancelled' };
      // eslint-disable-next-line no-await-in-loop
      const page = await fetchPageWithRetry(channel.id, afterSeq, { signal, sleep, onRateLimited });
      if (signal?.aborted) return { status: 'cancelled' };
      if (page.historyDays != null) historyDays = page.historyDays;
      for (const m of page.messages) {
        if (rows.length >= maxMessages) {
          truncated = true;
          break;
        }
        rows.push(toExportRow(m));
      }
      onProgress?.(rows.length);
      if (truncated || page.nextAfterSeq == null) break;
      if (rows.length >= maxMessages) {
        truncated = true;
        break;
      }
      afterSeq = page.nextAfterSeq;
    }
  } catch (err) {
    if (signal?.aborted || isCancel(err)) return { status: 'cancelled' };
    if (err instanceof RateLimitedError) {
      notify.error(t('chat.export.rateLimitedFailed'));
      return { status: 'error', reason: 'rate_limited' };
    }
    if (err?.response?.status === 402) {
      setUpgradeReason(err.response.data?.error || t('chat.export.upgradeReason'));
      setUpgradeModalOpen(true);
      return { status: 'upgrade' };
    }
    notify.error(t('chat.export.failed'));
    return { status: 'error' };
  }

  const spec = EXPORT_FORMATS[format] || EXPORT_FORMATS.markdown;
  const exportedAt = now();
  const meta = {
    channelName: channelName || channel.name || 'Chat',
    channelKind: channel.kind || null,
    exportedAt,
    exportedBy: useAuthStore.getState().user?.name || '',
    members: (channel.members || []).map((m) => memberInfo(m).name).filter(Boolean),
    truncated,
    historyDays,
  };
  try {
    const content = (FORMATTERS[format] || FORMATTERS.markdown)(rows, meta);
    const res = await saveTextFile({
      content,
      filename: exportFileName(meta.channelName, format, exportedAt),
      mime: spec.mime,
      filters: spec.filters,
      title: t('chat.export.dialogTitle'),
    });
    if (res?.cancelled) {
      notify.info(t('chat.export.cancelled'));
      return { status: 'cancelled' };
    }
    notify.success(res?.path ? `${t('chat.export.done')} ${res.path}` : t('chat.export.done'));
    if (truncated) notify.info(t('chat.export.truncated', { count: rows.length }));
    return { status: 'saved', count: rows.length, truncated, path: res?.path };
  } catch {
    notify.error(t('chat.export.failed'));
    return { status: 'error' };
  }
}
